import {
  changedRowSummaries,
  flattenFacts,
  hasNotificationCorrection,
  OMISSION_NOTE,
} from './evidence';
import { sourceExcerpt } from './source-excerpt';
import {
  displayStatus,
  fallbackVerdict,
  formatPropertyStreet,
  notificationButtonLabel,
  notificationChecks,
  notificationVerdicts,
  recordAbout,
  recordChange,
  recordName,
  recordWhere,
  strongerVerdict,
  subjectDescription,
  verdictFloor,
  type NotificationVerdictWord,
} from './present';
import { propertyRecordKindLabel } from '../field-labels';
import { formatPropertyAddress, formatPropertyAddressText } from '../presentation';
import { describeFieldChange, fieldDeltas, issueFieldLabel } from '../issues/index';
import type { IssueFactValue } from '../issues/types';
import type { NotificationDisplayEntry } from './types';

export * from './present';

/** Version 2 adds the verdict, what Ava checked, and each record's description, place and change. */
export const NOTIFICATION_BRIEF_VERSION = 2;
export const NOTIFICATION_BRIEF_LIMIT = 6;

/** The writer's action for the email: it picks the button's verb for corrections and unclear updates. */
export const notificationActions = {
  updates: 'See what changed',
  inspections: 'Review inspection results',
  correction: 'Review the correction',
  unclear: 'Review the unexplained update',
} as const;

/** Presentation facts can come from a journal event or an existing property alert. */
export type NotificationBriefEntry = Omit<NotificationDisplayEntry, 'source' | 'eventIds'> & {
  source: NotificationDisplayEntry['source'] | 'treasurer' | 'street-view';
  eventIds?: string[];
  linkKey?: string;
};

export type NotificationBriefInput = {
  entries: NotificationBriefEntry[];
  propertyAddresses: Record<string, string>;
  /** Platform-supplied display labels; never parse as addresses or evidence. */
  propertyNames?: Record<string, string>;
  totalUpdateCount: number;
  incompletePropertyCount: number;
};

export type NotificationBriefUpdate = {
  entryKey: string;
  /** The record's kind and official number, from the evidence. */
  title: string;
  /** What the record is about, from its own description, when the source gives one. */
  about?: string;
  /** Where the record is relative to the property, when the source says. */
  where?: string;
  /** The status change, for the before and after pills. */
  change?: { label: string; before: string; after: string };
  /** The change in plain words: the writer's, or the evidence's own in the fallback. */
  summary: string;
  linkKey: string;
  /** The writer's call on whether this update is worth the reader's attention; absent means yes. */
  worthTelling?: boolean;
  /** The writer's one-sentence reason when it judged the update not worth telling. */
  reason?: string;
};

export type NotificationBriefProperty = {
  propertyId: string;
  address: string;
  /** No action needed, heads up or action needed: never below what the evidence allows. */
  verdict: NotificationVerdictWord;
  /** The verdict in a sentence, including whether it is at the property. */
  headline: string;
  /** One supporting line: why Ava is telling the reader, or what is at stake. */
  summary: string;
  nextStep: string;
  /** What Ava checked, built from the evidence, never by the writer. */
  checks: string[];
  tone: 'good_news' | 'action' | 'unclear' | 'update';
  action: keyof typeof notificationActions;
  actionLinkKey: string | null;
  /** The button's specific verb, built from the evidence and the action. */
  button: string;
  updates: NotificationBriefUpdate[];
};

export type NotificationBrief = {
  version: 2;
  writer: 'ai' | 'fallback';
  /**
   * Before rendering, the subject's short description; the platform adds the
   * property or group name. A frozen brief carries the finished subject.
   */
  subject: string;
  preview: string;
  properties: NotificationBriefProperty[];
};

export function briefExcerpt(value: string, limit: number) {
  // Normalize control characters in untrusted city text before freezing display copy.
  // eslint-disable-next-line no-control-regex
  const normalized = value.replace(/[\u0000-\u001f\s]+/g, ' ').trim();
  const characters = Array.from(normalized);
  return characters.length <= limit ? normalized : `${characters.slice(0, limit - 1).join('')}…`;
}

/** Bound highlights and normalize property labels, never source evidence. */
export function notificationHighlights<T extends NotificationBriefInput>(input: T): T {
  return {
    ...input,
    entries: input.entries.slice(0, NOTIFICATION_BRIEF_LIMIT),
    propertyAddresses: Object.fromEntries(
      Object.entries(input.propertyAddresses).map(([id, address]) => [
        id,
        formatPropertyAddress(address),
      ]),
    ),
  };
}

/** Ava's name for a property in email: the saved address, never a parcel number. */
function propertyName(input: NotificationBriefInput, propertyId: string) {
  return briefExcerpt(input.propertyAddresses[propertyId] ?? 'your property', 180);
}

/** An update's deterministic parts: the record's name, what it is about, where, and its status change. */
function presentUpdate(entry: NotificationBriefEntry, summary: string): NotificationBriefUpdate {
  const about = recordAbout(entry);
  const where = recordWhere(entry);
  const change = recordChange(entry);
  return {
    entryKey: entry.key,
    title: briefExcerpt(formatPropertyAddressText(recordName(entry)), 180),
    ...(about ? { about } : {}),
    ...(where ? { where } : {}),
    ...(change ? { change } : {}),
    summary,
    linkKey: entryLinkKey(entry),
  };
}

/** The fallback's short subject: each change named from the evidence, never the property. */
function fallbackSubject(entries: NotificationBriefEntry[], updates: NotificationBriefUpdate[]) {
  if (entries.every((entry) => entry.issueIdentity === null)) return 'What I can check changed';
  const named = updates.map((update, index) => {
    const kind = propertyRecordKindLabel(entries[index]!.recordKind ?? 'record');
    return update.change ? `${kind} now ${update.change.after.toLowerCase()}` : `${kind} changed`;
  });
  return named.length <= 2 ? named.join(', ') : `${named.length} city records changed`;
}

/** Why the change matters, as far as the evidence alone can say. */
function fallbackContext(entries: NotificationBriefEntry[]) {
  if (entries.every((entry) => entry.issueIdentity === null))
    return 'The records and evidence I already have stay in AddAva.';
  const priority = entries.map((entry) => entry.priority);
  if (priority.includes('now')) return 'A deadline on this record is within a week.';
  if (priority.includes('soon')) return 'A deadline on this record is within a month.';
  if (priority.includes('review'))
    return 'The record is still open, so the change may affect what you need to do.';
  return 'Nothing on this record is due that I can see.';
}

const fallbackNextSteps: Record<NotificationVerdictWord, string> = {
  action_needed: 'Open the record to see what is due and when.',
  heads_up: 'Open the record to decide whether it needs a follow-up.',
  no_action: 'No action needed. I will keep watching.',
};

/**
 * The factual path writes the same email Ava would, from the evidence alone. It
 * names each record and change in plain words and judges nothing beyond what the
 * evidence's priority says; the model path adds judgement and context.
 */
export function fallbackNotificationBrief(input: NotificationBriefInput): NotificationBrief {
  input = notificationHighlights(input);
  const propertyIds = [...new Set(input.entries.map((entry) => entry.propertyId))];
  const subjects: string[] = [];
  const properties = propertyIds.map((propertyId): NotificationBriefProperty => {
    const entries = input.entries.filter((entry) => entry.propertyId === propertyId);
    const address = propertyName(input, propertyId);
    const sourceOnly = entries.every((entry) => entry.issueIdentity === null);
    const street = input.propertyNames?.[propertyId] || formatPropertyStreet(address);
    const updates = entries.map((entry) => presentUpdate(entry, notificationRecordSummary(entry)));
    const verdict = fallbackVerdict(entries);
    const first = updates[0]!;
    subjects.push(fallbackSubject(entries, updates));
    return {
      propertyId,
      address,
      verdict,
      headline: sourceOnly
        ? `What I can check for ${street} changed`
        : updates.length === 1
          ? first.change
            ? `${first.title} moved from ${first.change.before} to ${first.change.after}`
            : `${first.title} changed`
          : `${updates.length} city records changed for ${street}`,
      summary: fallbackContext(entries),
      nextStep: sourceOnly
        ? 'No action needed. I will keep checking the sources I can reach.'
        : fallbackNextSteps[verdict],
      checks: notificationChecks(entries, address, input.propertyNames?.[propertyId]),
      tone: 'update',
      action: 'updates',
      actionLinkKey: null,
      button: notificationButtonLabel({ verdict, action: 'updates', entries }),
      updates,
    };
  });
  const updates = properties.flatMap((property) => property.updates);
  const more = updates.length - 1;
  return {
    version: NOTIFICATION_BRIEF_VERSION,
    writer: 'fallback',
    subject: properties.length === 1 ? subjects[0]! : `Changes at ${properties.length} properties`,
    preview: briefExcerpt(
      `${updates[0]!.summary}${more > 0 ? ` ${more} more ${more === 1 ? 'record' : 'records'} changed.` : ''}`,
      180,
    ),
    properties,
  };
}

/** Copy can refer only to the exact selected properties/records. URLs and identities come from the application. */
export function validateNotificationBrief(
  value: unknown,
  input: NotificationBriefInput,
): NotificationBrief {
  input = notificationHighlights(input);
  const object = record(value, 'brief');
  const expectedProperties = [...new Set(input.entries.map((entry) => entry.propertyId))];
  // A single-property answer sometimes arrives flat, with the property's fields
  // at the root. Read it as that one property; every other check still applies.
  const flat =
    object.properties === undefined &&
    expectedProperties.length === 1 &&
    Array.isArray(object.updates)
      ? [
          {
            propertyId: expectedProperties[0],
            action: 'updates',
            actionEntryKey: null,
            ...object,
          },
        ]
      : null;
  const properties = array(flat ?? object.properties, 1, NOTIFICATION_BRIEF_LIMIT, 'properties');
  const seen = new Set<string>();
  const result = properties.map((item) => {
    const property = record(item, 'property');
    const propertyId = text(property.propertyId, 200, 'propertyId');
    if (seen.has(propertyId) || !expectedProperties.includes(propertyId))
      invalid('unexpected propertyId');
    seen.add(propertyId);
    const entries = input.entries.filter((entry) => entry.propertyId === propertyId);
    const seenEntries = new Set<string>();
    const updates = array(property.updates, 1, NOTIFICATION_BRIEF_LIMIT, 'updates').map((value) => {
      const update = record(value, 'update');
      const entryKey = text(update.entryKey, 2000, 'entryKey');
      const entry = entries.find((entry) => entry.key === entryKey);
      if (!entry || seenEntries.has(entryKey)) return invalid('unexpected entryKey');
      seenEntries.add(entryKey);
      // A "not worth telling" without a reason is ignored, never trusted.
      const silenced =
        update.worthTelling === false &&
        typeof update.reason === 'string' &&
        update.reason.trim().length > 0;
      return {
        ...presentUpdate(entry, prose(update.summary, 240, 'update summary', input.propertyNames)),
        ...(silenced
          ? {
              worthTelling: false,
              reason: prose(update.reason, 200, 'reason', input.propertyNames),
            }
          : update.worthTelling === true
            ? { worthTelling: true }
            : {}),
      };
    });
    if (seenEntries.size !== entries.length) invalid('missing updates');
    const action = choice(
      property.action,
      ['updates', 'inspections', 'correction', 'unclear'] as const,
      'action',
    );
    const written =
      property.verdict === undefined || property.verdict === null
        ? null
        : choice(property.verdict, notificationVerdicts, 'verdict');
    // The verdict now carries what tone used to; a writer that leaves tone out gets
    // the one its action and verdict imply rather than a rejected email.
    const tone =
      property.tone === undefined || property.tone === null
        ? action === 'correction' || written === 'action_needed'
          ? 'action'
          : action === 'unclear'
            ? 'unclear'
            : 'update'
        : choice(property.tone, ['good_news', 'action', 'unclear', 'update'] as const, 'tone');
    // A correction or unclear update with one record has only one possible target.
    const actionKey =
      property.actionEntryKey === null &&
      ['correction', 'unclear'].includes(action) &&
      entries.length === 1
        ? entries[0]!.key
        : property.actionEntryKey;
    const actionEntry =
      actionKey === null ? null : entries.find((entry) => entry.key === actionKey);
    if (actionEntry === undefined || (['correction', 'unclear'].includes(action) && !actionEntry))
      invalid('actionEntryKey');
    const supportedCorrection =
      action !== 'correction' || (actionEntry !== null && hasNotificationCorrection(actionEntry));
    const finalTone = supportedCorrection ? tone : 'update';
    // The writer decides how much the change matters; the evidence sets the floor.
    // A writer that gives no verdict gets the evidence's own (as the fallback would),
    // never a quiet "No action needed" under a headline that asks for something.
    // Evidence of a required correction always needs the reader, whatever the
    // writer's tone; an unexplained update is at least a heads up.
    const evidenceFloor: NotificationVerdictWord = entries.some(hasNotificationCorrection)
      ? 'action_needed'
      : action === 'unclear'
        ? 'heads_up'
        : 'no_action';
    const judged: NotificationVerdictWord[] = [
      written ?? fallbackVerdict(entries),
      toneVerdict[finalTone],
      evidenceFloor,
    ];
    const verdict = judged.reduce(strongerVerdict, verdictFloor(entries));
    const address = propertyName(input, propertyId);
    const finalAction = supportedCorrection ? action : 'updates';
    return {
      propertyId,
      address,
      verdict,
      // A title, not a sentence: no final period, and never empty once it is gone.
      headline:
        prose(property.headline, 90, 'headline', input.propertyNames).replace(/[.\s]+$/, '') ||
        invalid('headline'),
      summary: paragraphs(property.summary, 240, 'summary', input.propertyNames),
      nextStep: prose(property.nextStep, 260, 'nextStep', input.propertyNames),
      checks: notificationChecks(entries, address, input.propertyNames?.[propertyId]),
      tone: finalTone,
      action: finalAction,
      actionLinkKey: supportedCorrection && actionEntry ? entryLinkKey(actionEntry) : null,
      button: notificationButtonLabel({ verdict, action: finalAction, entries }),
      // Retain deterministic priority order, regardless of the model's order.
      updates: entries.map((entry) => updates.find((update) => update.entryKey === entry.key)!),
    };
  });
  if (seen.size !== expectedProperties.length) invalid('missing properties');
  const ordered = expectedProperties.map((id) => result.find((p) => p.propertyId === id)!);
  // The subject is a short description: the platform adds the property, so the
  // writer's own mention of it, and any verdict word, comes out.
  // A subject that was only a chip word takes the evidence's own description.
  const subject =
    subjectDescription(
      prose(object.subject, 90, 'subject', input.propertyNames),
      ordered.map((property) => property.address),
    ) ||
    fallbackSubject(
      input.entries.filter((entry) => entry.propertyId === ordered[0]!.propertyId),
      ordered[0]!.updates,
    );
  const preview = prose(object.preview, 180, 'preview', input.propertyNames);
  const same = (a: string, b: string) =>
    a.toLowerCase().replace(/\W+/g, '') === b.toLowerCase().replace(/\W+/g, '');
  return {
    version: NOTIFICATION_BRIEF_VERSION,
    writer: 'ai',
    subject,
    // A preview that only repeats the subject adds nothing; the first change says more.
    preview: same(preview, subject) ? briefExcerpt(ordered[0]!.updates[0]!.summary, 180) : preview,
    properties: ordered,
  };
}

const toneVerdict = {
  action: 'action_needed',
  unclear: 'heads_up',
  good_news: 'no_action',
  update: 'no_action',
} as const satisfies Record<NotificationBriefProperty['tone'], NotificationVerdictWord>;

/**
 * A frozen brief, as the dispatcher stored it. Version 1 briefs frozen before the
 * redesign still render: their verdict reads from the tone, and they have no checks
 * or record details. Malformed or unknown versions keep the historical evidence
 * usable and return null.
 */
export function readNotificationBrief(value: unknown): NotificationBrief | null {
  try {
    const brief = record(value);
    if (brief.version !== NOTIFICATION_BRIEF_VERSION && brief.version !== 1) return null;
    const legacy = brief.version === 1;
    // A version 2 brief was validated and formatted when it was written; reading it
    // back must return exactly what was frozen, or the prepared email would no longer
    // match the frozen subject. Only version 1 history is formatted again.
    const display = legacy ? formatPropertyAddressText : (value: string) => value;
    const writer = choice(brief.writer, ['ai', 'fallback'] as const);
    const properties = array(brief.properties, 1, 200).map((item): NotificationBriefProperty => {
      const property = record(item);
      const tone = choice(property.tone, ['good_news', 'action', 'unclear', 'update'] as const);
      const action = choice(
        property.action,
        Object.keys(notificationActions) as Array<keyof typeof notificationActions>,
      );
      return {
        propertyId: text(property.propertyId, 200),
        address: legacy
          ? formatPropertyAddress(text(property.address, 180))
          : text(property.address, 180),
        verdict: legacy ? toneVerdict[tone] : choice(property.verdict, notificationVerdicts),
        headline: display(text(property.headline, 280)),
        summary: paragraphText(property.summary, 840).split('\n\n').map(display).join('\n\n'),
        nextStep: display(text(property.nextStep, 520)),
        checks: legacy
          ? []
          : array(property.checks, 0, 4).map((check) => display(text(check, 300))),
        tone,
        action,
        actionLinkKey: property.actionLinkKey === null ? null : text(property.actionLinkKey, 200),
        button: legacy ? notificationActions[action] : text(property.button, 80),
        updates: array(property.updates, 1, 200).map((item): NotificationBriefUpdate => {
          const update = record(item);
          const change =
            !legacy && update.change !== undefined && update.change !== null
              ? record(update.change)
              : null;
          return {
            entryKey: text(update.entryKey, 2000),
            title: display(text(update.title, 180)),
            ...(!legacy && typeof update.about === 'string' && update.about
              ? { about: display(text(update.about, 240)) }
              : {}),
            ...(!legacy && typeof update.where === 'string' && update.where
              ? { where: text(update.where, 120) }
              : {}),
            ...(change
              ? {
                  change: {
                    label: text(change.label, 40),
                    before: text(change.before, 80),
                    after: text(change.after, 80),
                  },
                }
              : {}),
            summary: display(text(update.summary, 480)),
            linkKey: text(update.linkKey, 200),
            ...(typeof update.worthTelling === 'boolean'
              ? { worthTelling: update.worthTelling }
              : {}),
            ...(typeof update.reason === 'string' && update.reason
              ? { reason: display(text(update.reason, 400)) }
              : {}),
          };
        }),
      };
    });
    return {
      version: NOTIFICATION_BRIEF_VERSION,
      writer,
      subject: display(text(brief.subject, 320)),
      preview: display(text(brief.preview, 360)),
      properties,
    };
  } catch {
    return null;
  }
}

/**
 * A brief the dispatcher cached for this exact input: it reads back as stored
 * (already validated when written), and only when it covers exactly the same
 * properties and records. Anything else is regenerated.
 */
export function readCachedNotificationBrief(value: unknown, input: NotificationBriefInput) {
  const brief = readNotificationBrief(value);
  // A fallback is never reused: the next run asks the writer again.
  if (
    !brief ||
    brief.writer !== 'ai' ||
    (value as { version?: unknown }).version !== NOTIFICATION_BRIEF_VERSION
  )
    return null;
  const entries = notificationHighlights(input).entries;
  const expected = [...new Set(entries.map((entry) => entry.propertyId))];
  const same = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().every((item, i) => item === [...b].sort()[i]);
  if (
    !same(
      brief.properties.map((property) => property.propertyId),
      expected,
    )
  )
    return null;
  for (const property of brief.properties)
    if (
      !same(
        property.updates.map((update) => update.entryKey),
        entries
          .filter((entry) => entry.propertyId === property.propertyId)
          .map((entry) => entry.key),
      )
    )
      return null;
  return brief;
}

function entryLinkKey(entry: NotificationBriefEntry) {
  return entry.linkKey ?? entry.eventIds?.at(-1) ?? invalid();
}

/**
 * The update line names the record. A long source title is the record's own
 * description (a notice's violation text), so the line uses the record's kind
 * and official number instead; the summary carries what changed.
 */
export function notificationUpdateTitle(entry: NotificationBriefEntry) {
  if (sourceExcerpt(entry.title).omitted) return null;
  const name =
    entry.recordKind && entry.sourceRecordId
      ? `${propertyRecordKindLabel(entry.recordKind)} ${entry.sourceRecordId}`
      : null;
  return briefExcerpt(
    formatPropertyAddressText(name && Array.from(entry.title).length > 70 ? name : entry.title),
    180,
  );
}

export function notificationRecordSummary(entry: NotificationBriefEntry) {
  if (entry.issueIdentity === null) {
    const labels: Record<string, string> = {
      source_adopted:
        'I started tracking this source. Its existing records are history, not new issues.',
      source_parser_upgraded:
        'I can now read more of this source. I re-read its existing records without treating them as new issues.',
      source_scope_expanded:
        'This source now covers more records. The newly visible history is coverage, not new activity.',
      source_lost:
        'This source was unavailable during this check. The evidence I already have stays in AddAva.',
    };
    return briefExcerpt(
      entry.transitions
        .map((transition) => labels[transition] ?? 'What I can check from this source changed.')
        .join(' '),
      420,
    );
  }
  if (entry.after?.sourceState === 'not_listed' || entry.transitions.includes('record_not_listed'))
    return briefExcerpt(
      'The source no longer lists this record. That does not establish that it was resolved.',
      420,
    );
  if (entry.transitions.includes('record_added'))
    return briefExcerpt(
      entry.recordKind === 'tax-bill'
        ? 'A tax statement was added to the source.'
        : 'The source now lists this record.',
      420,
    );
  const statuses = entry.transitions.filter((value) => value.includes('→'));
  const rowChanges = changedRowSummaries(entry);
  const scope = { source: entry.source, recordKind: entry.recordKind };
  const clip = (value: string, max: number) => briefExcerpt(sourceExcerpt(value).text, max);
  // A changed object (a record's field map) is described leaf by leaf, each in the
  // skill's words; a changed scalar is one sentence. Twins of the same detail
  // (a record's date and its page's date) collapse to one sentence.
  const changes = [
    ...new Set(
      entry.rawChanges
        .filter(
          (change) =>
            change.path !== '$' &&
            !(Array.isArray(change.before) || Array.isArray(change.after)) &&
            !(statuses.length && change.path === 'status'),
        )
        .flatMap((change) =>
          typeof change.before === 'object' || typeof change.after === 'object'
            ? fieldDeltas(change.before, change.after, change.path, [], () => false)
                .filter((delta) => !delta.rows && !delta.structural)
                .map((delta) =>
                  describeFieldChange(delta.field, delta.before, delta.after, clip, scope),
                )
            : fact(change.before) === fact(change.after)
              ? [] // A detail that was missing and is still empty did not change for the reader.
              : [
                  describeFieldChange(
                    change.path,
                    fact(change.before),
                    fact(change.after),
                    clip,
                    scope,
                  ),
                ],
        ),
    ),
  ].slice(0, 3);
  const transitions = statuses.map((value) => {
    if (sourceExcerpt(value).omitted)
      return 'The status changed; the source wording is not repeated here';
    const [before, after] = value.split('→');
    return `Status changed from ${displayStatus(before ?? '')} to ${displayStatus(after ?? '')}`;
  });
  const sentences = [
    ...rowChanges.filter((text) => text === OMISSION_NOTE),
    ...transitions,
    ...rowChanges.filter((text) => text !== OMISSION_NOTE),
    ...changes,
  ];
  return briefExcerpt(
    formatPropertyAddressText(
      sentences.length
        ? sentences.map((sentence) => sentence.replace(/\.?$/, '.')).join(' ')
        : 'The source updated this record without a change I can name.',
    ),
    420,
  );
}

/** A scalar or small structure in plain words; never serialized JSON. */
function fact(value: unknown) {
  if (value === null || value === undefined || value === '') return null;
  const text =
    typeof value === 'string'
      ? value
      : typeof value === 'object'
        ? flattenFacts(value as IssueFactValue)
            .map((item) => (item.path ? `${issueFieldLabel(item.path)} ${item.value}` : item.value))
            .join(', ')
        : String(value);
  return briefExcerpt(sourceExcerpt(text).text, 100);
}

function invalid(reason = 'shape'): never {
  throw new Error(`Invalid notification brief: ${reason}`);
}
function record(value: unknown, field?: string) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid(field);
  return value as Record<string, unknown>;
}
function array(value: unknown, min: number, max: number, field?: string) {
  if (!Array.isArray(value) || value.length < min || value.length > max) return invalid(field);
  return value as unknown[];
}
function text(value: unknown, max: number, field?: string) {
  if (typeof value !== 'string' || !value.trim()) return invalid(field);
  if (Array.from(value).length > max) return invalid(`${field} too long`);
  // Email headers and stored single-line copy must not contain control characters.
  if (Array.from(value).some((character) => character.charCodeAt(0) < 32))
    return invalid(`${field} control character`);
  return value.trim();
}
/** Model prose may overflow the advised limit up to 2x before clipping; structure and identifiers stay strict. */
function prose(
  value: unknown,
  max: number,
  field?: string,
  propertyNames: Record<string, string> = {},
) {
  const copy = text(value, 2000, field);
  if (/https?:\/\/|www\.|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|<\/?[a-z][^>]*>/i.test(copy))
    invalid('prose contains a link, contact or markup');
  // Normalize source addresses around exact user labels, never inside a label.
  const names = [...new Set(Object.values(propertyNames).filter(Boolean))].sort(
    (a, b) => b.length - a.length,
  );
  let normalized = '';
  let offset = 0;
  while (offset < copy.length) {
    const next = names
      .map((name) => ({ name, at: copy.indexOf(name, offset) }))
      .filter((match) => match.at >= 0)
      .sort((a, b) => a.at - b.at)[0];
    if (!next) {
      normalized += formatPropertyAddressText(copy.slice(offset));
      break;
    }
    normalized += formatPropertyAddressText(copy.slice(offset, next.at)) + next.name;
    offset = next.at + next.name.length;
  }
  return briefExcerpt(normalized, max * 2);
}
/** Prose in up to three paragraphs, which the writer separates with line breaks. */
function paragraphs(
  value: unknown,
  max: number,
  field?: string,
  propertyNames?: Record<string, string>,
) {
  if (typeof value !== 'string') return invalid(field);
  const parts = value
    .split(/\r?\n+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 3);
  if (!parts.length) return invalid(field);
  return parts.map((part) => prose(part, max, field, propertyNames)).join('\n\n');
}
/** Stored paragraphs: each is single-line text, separated by one blank line. */
function paragraphText(value: unknown, max: number) {
  if (typeof value !== 'string') return invalid();
  const parts = value.split('\n\n');
  if (parts.length > 3) return invalid();
  return parts.map((part) => text(part, max)).join('\n\n');
}
function choice<T extends string>(value: unknown, choices: readonly T[], field?: string) {
  if (typeof value !== 'string' || !choices.includes(value as T)) return invalid(field);
  return value as T;
}
